import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './store/AuthContext';

import AuthLayout from './layouts/AuthLayout';
import AppLayout from './layouts/AppLayout';
import ProductAdminLayout from './layouts/ProductAdminLayout';

import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import VerifyOtp from './pages/auth/VerifyOtp';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

import Dashboard from './pages/Dashboard';
import Subscription from './pages/Subscription';
import Plans from './pages/Plans';
import Checkout from './pages/Checkout';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailed from './pages/PaymentFailed';
import PaymentHistory from './pages/PaymentHistory';
import Profile from './pages/Profile';
import Download from './pages/Download';
import ProductAdminLogin from './pages/product-admin/ProductAdminLogin';
import ProductAdminDashboard from './pages/product-admin/ProductAdminDashboard';
import ProductAdminOrganizations from './pages/product-admin/ProductAdminOrganizations';
import ProductAdminUsage from './pages/product-admin/ProductAdminUsage';
import ProductAdminManualRegistrations from './pages/product-admin/ProductAdminManualRegistrations';
import ProductAdminManualRegistrationNew from './pages/product-admin/ProductAdminManualRegistrationNew';
import ProductAdminManualRegistrationDetail from './pages/product-admin/ProductAdminManualRegistrationDetail';
import ProductAdminCompany from './pages/product-admin/ProductAdminCompany';
import ProductAdminToken from './pages/product-admin/ProductAdminToken';

import ErrorBoundary from './components/ErrorBoundary';
import Toast from './components/Toast';

function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/product-admin/login" element={<ProductAdminLogin />} />
          <Route element={<ProductAdminGuard><ProductAdminLayout /></ProductAdminGuard>}>
            <Route path="/product-admin/dashboard" element={<ProductAdminDashboard />} />
            <Route path="/product-admin/organizations" element={<ProductAdminOrganizations />} />
            <Route path="/product-admin/manual-registrations" element={<ProductAdminManualRegistrations />} />
            <Route path="/product-admin/manual-registrations/new" element={<ProductAdminManualRegistrationNew />} />
            <Route path="/product-admin/manual-registrations/:registrationId" element={<ProductAdminManualRegistrationDetail />} />
            <Route path="/product-admin/usage" element={<ProductAdminUsage />} />
            <Route path="/product-admin/company/:companyId" element={<ProductAdminCompany />} />
            <Route path="/product-admin/token/:tokenId" element={<ProductAdminToken />} />
          </Route>
          {/* Auth Routes */}
          <Route element={<PublicRoute><AuthLayout /></PublicRoute>}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-otp" element={<VerifyOtp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Route>

          {/* Protected Routes */}
          <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/subscription" element={<Subscription />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-failed" element={<PaymentFailed />} />
            <Route path="/payment-history" element={<PaymentHistory />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/download" element={<Download />} />
          </Route>

          {/* Default Redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <Toast />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

function ProductAdminGuard({ children }) {
  return localStorage.getItem('productAdminToken') ? children : <Navigate to="/product-admin/login" replace />;
}

export default App;
